import * as React from 'react';

import type { ComplianceAuditExportFilter } from '@/api/exports-client';
import { useExportsClient } from '@/api/exports-client-context';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// =============================================================================
// compliance-audit-screen.tsx — Écran ADMIN « Export conformité » (R15-GOV-06, DBU §1.4.4 / ADR D12).
// =============================================================================
// « Export avancé compliance audit log JSON structuré » (V1.5). L'admin choisit une PÉRIODE (du / au) et,
// optionnellement, un TYPE d'événement, puis déclenche un TÉLÉCHARGEMENT DIRECT du document JSON structuré
// des entrées d'audit de cette période (GET /admin/audit/export.json?from&to&type). γ SYNCHRONE (parité
// exports CSV S10) : pas de job/polling, pas de Domain Event. L'autorité (RBAC AdminSessionGuard, lecture
// GLOBALE audit_entry, tri occurredAt DESC) reste l'API.
//
// Le déclenchement passe par l'ExportsClient (DI seam) : impl navigateur réelle en prod (ancre invisible),
// double déterministe en test (capture l'URL FILTRÉE). Le filtre courant (période/type) est PROPAGÉ aux
// query params — display == export (DoD R3).
//
// DOCTRINE NON-NOMINATIF (SECOPS) : le document produit ne contient AUCUNE donnée nominative (ni tenant_id,
// ni ip, ni payload brut) — seulement l'enveloppe traçable (type/date/auteur opaque). L'écran le rappelle.
//
// DESIGN SYSTEM : titre de page serif (main h1), Card DS, champs DS (Label + Input date + select DS),
// bouton DS. AUCUNE UI hors système.
//
// WCAG 2.1 AA : chaque champ porte un <Label> lié (htmlFor) ; le bouton porte un libellé explicite ;
// période obligatoire (bouton désactivé tant que « du »/« au » ne sont pas saisis) avec message d'aide lié ;
// navigation clavier native ; aucune dépendance couleur.

/** Types d'événement filtrables (libellés FR lisibles). Clé stable = type technique, indépendante de l'i18n. */
const EVENT_TYPE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'DE.AnalysisPublishedToPulse', label: 'Publication du benchmark (Pulse)' },
  { value: 'DE.BenchmarkExported', label: 'Export du benchmark' },
  { value: 'DE.PanelFrozen', label: 'Figement du panel' },
  { value: 'DE.CampaignClosed', label: 'Clôture définitive de la campagne' },
  { value: 'DE.CampaignAbandoned', label: 'Abandon de la campagne' },
  { value: 'DE.SubmissionLocked', label: 'Soumission finale verrouillée' },
  { value: 'DE.CguVersionPublished', label: 'Nouvelle version CGU publiée' },
];

interface FilterState {
  readonly from: string;
  readonly to: string;
  /** Type sélectionné, ou '' pour « Tous les types ». */
  readonly type: string;
}

const EMPTY_FILTER: FilterState = { from: '', to: '', type: '' };

/** Convertit l'état de filtre en filtre d'export (types = liste 0..1 ; axes vides omis). */
function toExportFilter(f: FilterState): ComplianceAuditExportFilter {
  return {
    ...(f.from !== '' ? { from: f.from } : {}),
    ...(f.to !== '' ? { to: f.to } : {}),
    ...(f.type !== '' ? { types: [f.type] } : {}),
  };
}

export function ComplianceAuditScreen(): React.JSX.Element {
  const exportsClient = useExportsClient();
  const [filter, setFilter] = React.useState<FilterState>(EMPTY_FILTER);

  // Le back exige une PÉRIODE : le bouton reste désactivé tant que « du »/« au » ne sont pas saisis.
  const periodComplete = filter.from !== '' && filter.to !== '';
  // Garde d'ordre côté UI (le back rejette aussi from>to en 400) : évite un export voué à l'échec.
  const rangeValid = !periodComplete || filter.from <= filter.to;
  const canExport = periodComplete && rangeValid;

  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  const onExport = (): void => {
    if (!canExport) {
      return;
    }
    exportsClient.downloadComplianceAudit(toExportFilter(filter));
  };

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Export conformité
        </h1>
        <p className="text-muted-foreground">
          Exportez le journal d’audit d’une période au format JSON structuré. Le
          document ne contient aucune donnée nominative.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Journal d’audit — période</CardTitle>
          <CardDescription id="compliance-audit-help">
            Choisissez une période (obligatoire) et, si besoin, un type
            d’événement. L’export produit un document JSON des seules entrées de
            la période, de la plus récente à la plus ancienne.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="compliance-audit-from">Période — du</Label>
              <Input
                id="compliance-audit-from"
                type="date"
                value={filter.from}
                onChange={(e) =>
                  setFilter({ ...filter, from: e.target.value })
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="compliance-audit-to">Période — au</Label>
              <Input
                id="compliance-audit-to"
                type="date"
                value={filter.to}
                onChange={(e) => setFilter({ ...filter, to: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="compliance-audit-type">Type d’événement</Label>
              <select
                id="compliance-audit-type"
                className={selectClass}
                value={filter.type}
                onChange={(e) =>
                  setFilter({ ...filter, type: e.target.value })
                }
              >
                <option value="">Tous les types</option>
                {EVENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {periodComplete && !rangeValid ? (
            <p role="alert" className="text-sm text-destructive">
              La date de début doit précéder ou égaler la date de fin.
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={onExport}
              disabled={!canExport}
              aria-describedby="compliance-audit-help"
              aria-label="Télécharger l'export JSON de conformité du journal d'audit"
            >
              Exporter le JSON
            </Button>
            {!periodComplete ? (
              <span role="status" className="text-sm text-muted-foreground">
                Sélectionnez une période pour activer l’export.
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
